import { useEffect, useState } from "react";
import { api } from "../api/apiClient";
import type { ItemCategory } from "../api/types";
import { useAuth } from "../state/AuthContext";
import { NewCategoryFields } from "./NewCategoryFields";

/**
 * Sentinel `<option>` value meaning "create a NEW category", as opposed to
 * assigning one that already exists.
 *
 * These used to be the same thing: picking the "Other" CATEGORY was treated
 * as "create a new category", so an item genuinely filed under Other could
 * never be edited without spawning a duplicate — and once G-30 made category
 * creation owner-only, a manager editing such an item could not save at all.
 * The two concerns are now separate: Other is an ordinary category anyone may
 * assign, and creating one is this explicit, owner-only choice.
 */
export const CREATE_NEW_CATEGORY = "__create_new__";

interface CategoryPickerProps {
  value: string;
  onChange: (next: string) => void;
  /** Name for the to-be-created category (only when value === CREATE_NEW_CATEGORY). */
  newCategoryLabel: string;
  onNewCategoryLabelChange: (next: string) => void;
  /** G-30: VAT percent for the to-be-created category, as a draft string. */
  newCategoryVatPercent: string;
  onNewCategoryVatPercentChange: (next: string) => void;
  /** G-45: target margin for the to-be-created category, as a draft string. */
  newCategoryTargetMarginPercent: string;
  onNewCategoryTargetMarginPercentChange: (next: string) => void;
  listVersion?: number;
  required?: boolean;
}

export function CategoryPicker({
  value,
  onChange,
  newCategoryLabel,
  onNewCategoryLabelChange,
  newCategoryVatPercent,
  onNewCategoryVatPercentChange,
  newCategoryTargetMarginPercent,
  onNewCategoryTargetMarginPercentChange,
  listVersion = 0,
  required = false,
}: CategoryPickerProps) {
  const { hasRole } = useAuth();
  // G-30: creating a category means setting its VAT rate, i.e. setting tax —
  // owner-only, enforced by the API too. Assigning an existing category
  // (including Other) stays open to managers.
  const canCreateCategory = hasRole("OWNER");
  const [categories, setCategories] = useState<ItemCategory[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    api
      .listCategories()
      .then((rows) => {
        if (!cancelled) setCategories(rows);
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : "Could not load categories");
        }
      });
    return () => {
      cancelled = true;
    };
  }, [listVersion]);

  const creatingNew = value === CREATE_NEW_CATEGORY;

  return (
    <div className="field">
      <label htmlFor="categorySelect">Category{required ? " *" : ""}</label>
      <select
        id="categorySelect"
        value={value}
        onChange={(e) => {
          const next = e.target.value;
          onChange(next);
          if (next !== CREATE_NEW_CATEGORY) {
            onNewCategoryLabelChange("");
          }
        }}
        required={required}
        aria-describedby={creatingNew ? "newCategoryHint" : undefined}
      >
        <option value="">Select a category</option>
        {categories.map((cat) => (
          <option key={cat.id} value={cat.id}>
            {cat.label}
          </option>
        ))}
        {canCreateCategory ? (
          <option value={CREATE_NEW_CATEGORY}>+ Create new category…</option>
        ) : null}
      </select>
      {creatingNew ? (
        <NewCategoryFields
          label={newCategoryLabel}
          onLabelChange={onNewCategoryLabelChange}
          vatPercent={newCategoryVatPercent}
          onVatPercentChange={onNewCategoryVatPercentChange}
          targetMarginPercent={newCategoryTargetMarginPercent}
          onTargetMarginPercentChange={onNewCategoryTargetMarginPercentChange}
        />
      ) : null}
      {error ? <div className="alert error">{error}</div> : null}
    </div>
  );
}
